import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {ProductStatus} from '@services/model/product';
import {ReactiveFormsModule, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {combineLatest, Observable, Subject, Subscription} from 'rxjs';
import {distinctUntilChanged, map, share, startWith, tap} from 'rxjs/operators';
import {ContinuousActionComponent} from '@widgets/continuous-action/continuous-action.component';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {repeatIf} from '@util/rxjs';
import {ProductQuery, SearchProductResponse, SearchProductSort, VerdictFilter} from '@services/resource/search-product-resources';
import {SearchproductService} from '@services/searchproduct.service';
import {Cycle} from '@services/model/cycle';
import {CycleService} from '@services/cycle.service';
import {VoilaUserService} from '@services/voila-user.service';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator} from '@angular/material/paginator';
import {DownloadDialogConfig, ExcelDownloadDialogComponent} from '@ma-dashboard/excel-export/excel-download-dialog/excel-download-dialog.component';
import {UserType} from '@services/resource/user-type';
import {Pagination} from '@util/pagination';
import {BusinessInitiationService} from '@services/business-initiation.service';
import {MarkantElementsModule} from '@markant/elements-angular';
import {CommonModule} from '@angular/common';
import {ProductGroupSelectComponent} from '@app/product-group-select/product-group-select.component';
import {ProductEntryComponent} from '@app/product-list/product-entry/product-entry.component';
import {ProductEntryWidgetContext} from '@app/product-list/product-entry/product-entry-widget';

@Component({
  selector: 'app-product-list',
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MarkantElementsModule,
    ProductGroupSelectComponent,
    ContinuousActionComponent,
    ProductEntryComponent,
    MatPaginator,
  ],
})
export class ProductListComponent implements OnInit, OnDestroy {
  @Input() fixStatusIn?: ProductStatus[];
  @Input() fixCycle?: string;
  @Input() fixPublished?: boolean;
  @Input() fixAnyVerdict?: boolean;
  @Input() fixAnyWarenbereich?: boolean;
  @Input() fixPageSize?: number;
  @Input() fixProductSort?: SearchProductSort;
  @Input() fixSingleCycle = false;

  @Input() defaultSort?: SearchProductSort;

  @Input() hideFilters = false;
  @Input() skipQueryParams = false;
  @Input() hidePagination = false;
  @Input() hideExcelExport = false;
  @Input() hideBrExport = false;
  @Input() scrollOnPageChange = true;

  @Input() actionsTemplate?: TemplateRef<any>;
  @Input() entryWidget?: TemplateRef<ProductEntryWidgetContext>;

  @Output() queryChanged: EventEmitter<ProductQuery> = new EventEmitter();

  cycles$: Observable<Cycle[]>;
  query$: Observable<ProductQuery>;
  response$: Observable<SearchProductResponse>;
  manualReload$: Subject<void> = new Subject<void>();
  offsetAndLimit$: Observable<Pagination>;

  public hasPreviousPage$: Observable<boolean>;
  public hasNextPage$: Observable<boolean>;

  private filterSub: Subscription;

  form = new UntypedFormGroup({
    productGroupKey: new UntypedFormControl('all'),
    statusIn: new UntypedFormControl('all'),
    searchProductSort: new UntypedFormControl(this.defaultSort),
    cycleFilter: new UntypedFormControl('all'),
    verdictFilter: new UntypedFormControl('all'),
    text: new UntypedFormControl(''),
    favorite: new UntypedFormControl('all'),
  });

  @ViewChild('loading', {static: true})
  continuous: ContinuousActionComponent;

  @ViewChild(MatPaginator, {static: true})
  paginator: MatPaginator;

  constructor(
    private readonly cycleService: CycleService,
    private readonly searchproductService: SearchproductService,
    private readonly businessInitiationService: BusinessInitiationService,
    private readonly userService: VoilaUserService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.updateFormValuesFromParams(this.route.snapshot.queryParams);

    /* generateExport paginator values */
    this.offsetAndLimit$ = this.paginator.page.pipe(
      map((e) => {
        const offset = e.pageSize * e.pageIndex;
        const limit = e.pageSize;
        return new Pagination(offset, limit);
      }),
      tap(() => this.scrollOnPageChange && window.scrollTo(0, 0)),
      startWith(new Pagination(0, this.fixPageSize || 60)),
    );

    if (this.skipQueryParams) {
      this.query$ = this.setupFlowDirect().pipe(tap((q) => this.queryChanged.emit(q)));
    } else {
      this.query$ = this.setupFlowViaQueryParams().pipe(tap((q) => this.queryChanged.emit(q)));
    }
    this.response$ = this.query$.pipe(
      repeatIf(this.manualReload$),
      this.continuous.actions((q) => this.searchproductService.search(q)),
      share(),
    );
    /* query the visible cycles from the backend */
    this.cycles$ = this.cycleService.getAll(false);

    this.hasNextPage$ = this.response$.pipe(
      map((r) => {
        return r.query.offset + r.results.length < r.total;
      }),
    );
    this.hasPreviousPage$ = this.response$.pipe(
      map((r) => {
        return r.query.offset > 0;
      }),
    );
  }

  private setupFlowViaQueryParams(): Observable<ProductQuery> {
    /*  Flow: form -> queryParams */
    this.filterSub = this.form.valueChanges
      .pipe(
        map(() => this.form.value),
        distinctUntilChanged((x, y) => JSON.stringify(x) === JSON.stringify(y)),
      )
      .subscribe((formValues) => {
        this.paginator.firstPage();
        this.router.navigate([], {queryParams: formValues});
      });
    /* Flow: queryParams -> query */
    return combineLatest([this.route.queryParams, this.offsetAndLimit$]).pipe(
      tap((x) => this.updateFormValuesFromParams(x[0])),
      map((x) => this.mkQuery(x[0], x[1])),
    );
  }

  private setupFlowDirect(): Observable<ProductQuery> {
    /* Flow: queryParams -> query */
    const formValues$ = this.form.valueChanges.pipe(
      startWith(undefined),
      map(() => this.form.value),
    );
    return combineLatest([formValues$, this.offsetAndLimit$]).pipe(map((x) => this.mkQuery(x[0], x[1])));
  }

  ngOnDestroy(): void {
    if (this.filterSub) {
      this.filterSub.unsubscribe();
    }
  }

  updateList(): void {
    this.manualReload$.next();
  }

  private updateFormValuesFromParams(queryParams: Params): void {
    this.form.setValue({
      productGroupKey: this.fixAnyWarenbereich || queryParams.productGroupKey || 'all',
      statusIn: this.fixStatusIn || queryParams.statusIn || 'all',
      searchProductSort: this.fixProductSort || queryParams.searchProductSort || 'productCreatedDesc',
      cycleFilter: this.fixCycle || queryParams.cycleFilter || 'all',
      verdictFilter: queryParams.verdictFilter || 'all',
      text: queryParams.text || '',
      favorite: queryParams.favorite || 'all',
    });
  }

  private mkQuery(queryParams: Params, pagination: Pagination): ProductQuery {
    const companyId = this.userService.currentUser().companyId;
    const request: ProductQuery = {
      offset: pagination.offset,
      limit: this.fixPageSize || pagination.limit,
      statusIn: this.fixStatusIn || (queryParams.statusIn && queryParams.statusIn !== 'all' ? this.asList(queryParams.statusIn) : null),
      startupId: queryParams.startupId || null,
      userId: null,
      published: this.fixPublished || null,
      productGroupKey: !queryParams.productGroupKey || queryParams.productGroupKey === 'all' ? null : queryParams.productGroupKey,
      cycleFilter: this.fixCycle || queryParams.cycleFilter || null,
      searchProductSort: this.fixProductSort || queryParams.searchProductSort || null,
      verdictFilter:
        !queryParams.verdictFilter || queryParams.verdictFilter === 'all'
          ? VerdictFilter.fromString('any', companyId)
          : VerdictFilter.fromString(queryParams.verdictFilter, companyId),
      text: queryParams.text || null,
      favorite: !queryParams.favorite || queryParams.favorite === 'all' ? null : queryParams.favorite,
    };
    return request;
  }

  private asList(x: undefined | string | [string]): ProductStatus[] | null {
    if (!x) {
      return null;
    }
    if (Array.isArray(x)) {
      return x as ProductStatus[];
    }
    return [x as ProductStatus];
  }

  excelExport(query: ProductQuery): void {
    const fileName = 'start-up-liste.xlsx';
    const downloadResponse = this.searchproductService.generateExport(query);
    const data: DownloadDialogConfig = {fileName, downloadResponse};
    this.dialog.open(ExcelDownloadDialogComponent, {data});
  }

  businessRelationExport(query: ProductQuery): void {
    const fileName = 'business-relations.xlsx';
    const downloadResponse = this.businessInitiationService.generateExport(query);
    const data: DownloadDialogConfig = {fileName, downloadResponse};
    this.dialog.open(ExcelDownloadDialogComponent, {data});
  }

  get excelExportEnabled(): boolean {
    const user = this.userService.currentUser();
    return user && user.userType === 'MA' && !this.hideExcelExport;
  }

  public nextPage(): void {
    this.paginator.nextPage();
  }

  public previousPage(): void {
    this.paginator.previousPage();
  }

  get isMa() {
    const user = this.userService.currentUser();
    return user && user.userType === UserType.MA;
  }
}
